import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// eslint-disable-next-line no-unused-vars
import autoTable from 'jspdf-autotable';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format currency helper
const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
};

// Get column headers based on visible columns
const getColumnHeaders = (visibleColumns, columnLabels) => {
  const headers = [];
  const keys = [];

  Object.entries(columnLabels).forEach(([key, label]) => {
    if (visibleColumns[key] && key !== 'action') {
      headers.push(label);
      keys.push(key);
    }
  });

  return { headers, keys };
};

// Get asset data for export
const getAssetData = (asset, keys) => {
  const row = [];
  
  keys.forEach(key => {
    let value = 'N/A';
    
    switch (key) {
      case 'image':
        value = asset.photos && asset.photos.length > 0 ? 'Yes' : 'No';
        break;
      case 'assetTag':
        value = asset.assetTag || 'N/A';
        break;
      case 'description':
        value = asset.description || 'N/A';
        break;
      case 'brand':
        value = asset.brand || 'N/A';
        break;
      case 'model':
        value = asset.model || 'N/A';
        break;
      case 'serialNumber':
        value = asset.serialNumber || 'N/A';
        break;
      case 'status':
        value = asset.status ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1).replace('_', ' ') : 'N/A';
        break;
      case 'purchaseDate':
        value = formatDate(asset.purchaseDate);
        break;
      case 'cost':
        value = formatCurrency(asset.cost);
        break;
      case 'purchasedFrom':
        value = asset.purchasedFrom || 'N/A';
        break;
      case 'site':
        value = asset.location?.site?.name || 'N/A';
        break;
      case 'location':
        value = asset.location?.locationDetail || 'N/A';
        break;
      case 'category':
        value = asset.category || 'N/A';
        break;
      case 'department':
        value = asset.location?.department?.name || 'N/A';
        break;
      case 'assignedTo':
        value = asset.assignedTo?.name || 'N/A';
        break;
      case 'createdAt':
        value = formatDate(asset.createdAt);
        break;
      default:
        value = 'N/A';
    }
    
    row.push(value);
  });
  
  return row;
};

// Export to Excel
export const exportToExcel = (assets, visibleColumns, columnLabels, filename = 'assets') => {
  try {
    const { headers, keys } = getColumnHeaders(visibleColumns, columnLabels);
    
    // Prepare data
    const data = [headers];
    assets.forEach(asset => {
      data.push(getAssetData(asset, keys));
    });
    
    // Create workbook
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    const colWidths = headers.map((_, index) => {
      const maxLength = Math.max(
        headers[index].length,
        ...assets.map(asset => {
          const value = getAssetData(asset, keys)[index];
          return value ? value.toString().length : 0;
        })
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    ws['!cols'] = colWidths;
    
    // Create workbook and add sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assets');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const excelFilename = `${filename}_${timestamp}.xlsx`;
    
    // Write file
    XLSX.writeFile(wb, excelFilename);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

// Export to PDF
export const exportToPDF = (assets, visibleColumns, columnLabels, filename = 'assets') => {
  try {
    const { headers, keys } = getColumnHeaders(visibleColumns, columnLabels);
    
    // Prepare data
    const data = assets.map(asset => getAssetData(asset, keys));
    
    // Create PDF
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Add title
    doc.setFontSize(16);
    doc.text('Asset Management Report', 14, 15);
    
    // Add date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);
    doc.text(`Total Assets: ${assets.length}`, 14, 27);
    
    // Add table using autoTable
    // Note: autoTable extends jsPDF prototype, so doc.autoTable should be available
    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 32,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [102, 126, 234],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      margin: { top: 32, right: 14, bottom: 20, left: 14 },
      tableWidth: 'auto',
    });
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const pdfFilename = `${filename}_${timestamp}.pdf`;
    
    // Save PDF
    doc.save(pdfFilename);
    
    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};

// Prepare data for print
export const preparePrintData = (assets, visibleColumns, columnLabels) => {
  const { headers, keys } = getColumnHeaders(visibleColumns, columnLabels);
  const data = assets.map(asset => getAssetData(asset, keys));
  
  return { headers, data, count: assets.length };
};

